import React from "react";
import "../DashboardStyles.css";

const EMPLOYERS = [
  ["Regional West Health Services / Regional West Medical Center", "Healthcare", "Scotts Bluff; regional referral center", "1,850+"],
  ["BNSF Railway", "Transportation / Rail Logistics", "Box Butte / Alliance; multi-county rail operations", "1,400+"],
  ["Sidney Regional Medical Center", "Healthcare / Critical Access Hospital", "Cheyenne; southern Panhandle healthcare anchor", "559"],
  ["Western Nebraska Community College", "Higher Education", "Regional; campuses in Scottsbluff, Alliance, and Sidney", "516"],
  ["Scottsbluff Public Schools", "Education, K–12", "Scotts Bluff", "500+"],
  ["Wal-Mart", "Retail", "Scotts Bluff", "450"],
  ["Legacy Cooperative", "Agriculture / Retail / Energy", "Multi-county; western Nebraska and eastern Wyoming", "350+"],
  ["Box Butte General Hospital", "Healthcare / Critical Access Hospital", "Box Butte; northern Panhandle healthcare anchor", "300+"],
  ["Parker-Hannifin Corporation", "Manufacturing", "Box Butte / Alliance", "285"],
  ["Chadron State College", "Higher Education", "Dawes; northern Panhandle higher-education anchor", "284"],
  ["Scotts Bluff County", "Local Government", "Scotts Bluff", "280"],
  ["Gering Public Schools", "Education, K–12", "Scotts Bluff", "272"],
  ["Western Sugar Cooperative", "Agribusiness / Food Manufacturing", "Scotts Bluff", "250–285"],
  ["Community Action Partnership of Western Nebraska", "Human Services / Nonprofit", "Regional; Scotts Bluff-based service provider", "216"],
  ["Alliance Public Schools", "Education, K–12", "Box Butte", "208"],
  ["Morrill County Community Hospital and Clinics", "Healthcare / Critical Access Hospital", "Morrill; Bridgeport/Bayard healthcare anchor", "~200"],
  ["Sidney Public Schools", "Education, K–12", "Cheyenne", "188"],
  ["Educational Service Unit 13", "Education Services", "Regional; serves Panhandle school systems", "186"],
  ["Chadron Hospital (formerly Chadron Community Hospital & Health Services)", "Healthcare / Critical Access Hospital", "Dawes; northern Panhandle healthcare anchor", "180+"],
  ["Region I Office of Human Development", "Human Services / Disability Services", "Multi-county; offices in Scottsbluff, Chadron, Alliance, and Sidney", "110"],
  ["Kelley Bean Company", "Agribusiness / Food Processing", "Scotts Bluff / Morrill / multi-state", "100+ local; 230+ companywide"],
  ["Kimball Health Services", "Healthcare / Critical Access Hospital", "Kimball; southern Panhandle healthcare anchor", "100+"],
  ["Gordon Memorial Hospital District", "Healthcare / Critical Access Hospital", "Sheridan; northern rural healthcare anchor", "90+"],
  ["Garden County Schools", "Education, K–12", "Garden; county anchor institution", "59"],
  ["Creek Valley Schools", "Education, K–12", "Deuel; county anchor institution", "50"],
  ["Banner County Public Schools", "Education, K–12", "Banner; county anchor institution", "33"],
  ["Sioux County Public Schools", "Education, K–12", "Sioux; county anchor institution", "30"],
];

export default function Chapter5Dashboard() {
  return (
    <div className="chapter chapter5">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 5</p>
        <h2>Regional Economy</h2>

        <h3>5.1 Introduction</h3>
        <p>
          Effective economic development begins with an honest assessment of
          the regional economy. This chapter provides that foundation — an
          integrated analysis of the region’s economic structure, industry
          sectors, workforce conditions, major employers, trends, and emerging
          opportunities — forming, together with the preceding chapters, the
          factual base from which the goals, objectives, and strategies of this
          CEDS are derived.
        </p>

        <h3>5.2 Regional Economic Overview</h3>
        <p>
          The Nebraska Panhandle functions as a predominantly rural,
          resource-dependent economy organized around a single micropolitan
          hub. The Scottsbluff-Gering metropolitan area serves as the undisputed
          economic center of the eleven-county region, concentrating the
          majority of the region’s healthcare, retail, financial, professional,
          and educational employment. Scotts Bluff County alone accounts for
          approximately 43 percent of the regional population, and its economic
          weight in employment, business establishments, and taxable sales is
          greater still. Each county plays a distinct role: Scotts Bluff as
          commercial and healthcare hub; Box Butte (Alliance) as northern
          agricultural trade center; Dawes (Chadron) anchored by Chadron State
          College and the Pine Ridge gateway; Cheyenne (Sidney) benefiting from
          oil and gas activity; and the remaining counties predominantly
          agricultural. Most frontier and rural counties have lost population in
          every decennial census since at least 1980.
        </p>

        <h3>5.3 Industry Sectors and Clusters</h3>
        <p>
          The Panhandle’s economic activity is concentrated in a relatively
          small number of sectors — a characteristic of both competitive
          strength and structural vulnerability.
        </p>
        <ul className="ceds-list">
          <li>
            <strong>Agriculture and Agribusiness</strong> — the foundational
            sector. Western Sugar Cooperative is the largest agribusiness
            employer; the dry edible bean sector is nationally recognized, with
            Nebraska ranking among the top three states.
          </li>
          <li>
            <strong>Healthcare and Social Services</strong> — Regional West
            Medical Center employs an estimated 1,500+ workers; the sector has
            grown as a share of regional employment, partially offsetting
            agricultural and retail losses, though it creates dependency on
            federal reimbursement.
          </li>
          <li>
            <strong>Retail Trade and Services</strong> — the Scottsbluff-Gering
            metro draws shoppers from Wyoming and northeastern Colorado;
            smaller-community retail faces e-commerce and population pressure.
          </li>
          <li>
            <strong>Government and Education</strong> — federal employment
            (USFS, NPS, BLM) and Chadron State College (~2,500 students) and
            Western Nebraska Community College anchor the base.
          </li>
          <li>
            <strong>Tourism and Hospitality</strong> — combined Scotts Bluff and
            Agate Fossil Beds visitor spending of $15.3 million in 2023
            supported 171 jobs, with strong seasonal concentration
            (May–September).
          </li>
          <li>
            <strong>Energy</strong> — conventional oil and gas, coal transport
            via BNSF, and a largely unrealized renewable opportunity that could
            generate construction jobs, O&amp;M employment, lease income, and
            property-tax revenue in fiscally stressed counties.
          </li>
        </ul>

        <h3>5.4 Labor Force and Workforce Conditions</h3>
        <p>
          The Panhandle’s labor force reflects its demographic profile — older,
          less formally educated, and declining in size. County-level labor
          force data make this contraction concrete. Between 2013 and 2023, the
          civilian labor force fell in every Panhandle county for which trend
          data is available: Scotts Bluff County’s labor force declined 5.8
          percent (from 19,094 to 17,990), while Box Butte County’s labor force
          contracted even faster, at 11.6 percent (5,968 to 5,274). Morrill
          (-3.5%) and Sioux (-5.3%) counties saw more moderate declines.
          Nebraska’s statewide labor force grew 3.9 percent over the same period
          (1,014,830 to 1,054,681) — meaning the Panhandle’s labor force
          contraction is a regional trend running directly counter to the
          state’s overall direction, not a shared national or state-level
          pattern the region happens to share. Unemployment rates have
          historically tracked near or below state and national averages, but
          this often reflects a discouraged-worker effect and out-migration of
          working-age adults rather than a tight, high-wage labor market. The
          10.5-point gap in bachelor’s degree attainment reflects both the
          historical economic structure and ongoing out-migration of
          college-educated young adults. WNCC and Chadron State are the primary
          institutional mechanisms for developing local talent, but both face
          the challenge of training workers who then leave the region.
        </p>
        <p className="source-note">
          Source note: county-level labor force figures are drawn from the U.S.
          Bureau of Labor Statistics, Local Area Unemployment Statistics (LAUS)
          program, as compiled in the City of Scottsbluff 2026 Comprehensive
          Plan, Table 5.1 (Labor Force, 2013–2023).
        </p>

        <h3>5.5 Major Employers</h3>
        <p>
          The following table presents an estimated inventory of the region’s
          major employers by sector.
        </p>
        <div className="table-wrapper">
          <table className="ceds-table">
            <thead>
              <tr>
                <th scope="col">Employer / Anchor Institution</th>
                <th scope="col">Sector</th>
                <th scope="col">County / Regional Role</th>
                <th scope="col">Est. Employees</th>
              </tr>
            </thead>
            <tbody>
              {EMPLOYERS.map(([name, sector, role, emp]) => (
                <tr key={name}>
                  <td><strong>{name}</strong></td>
                  <td>{sector}</td>
                  <td>{role}</td>
                  <td>{emp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="table-note">
          Sources: estimates based on publicly available data, institutional
          annual reports, and local chamber directories.
        </p>

        <h3>5.6 Economic Trends and Challenges</h3>
        <p>
          The Panhandle’s economy faces several interrelated structural trends:
          agricultural employment contraction due to mechanization and
          consolidation; healthcare’s growing role as a structural employment
          offset (genuine strength, but federal-reimbursement dependency);
          self-reinforcing population-driven demand contraction; and brain drain
          and workforce aging — the departure of college-educated young adults —
          as one of the most consequential long-term challenges.
        </p>

        <h3>5.7 Target Industries and Growth Opportunities</h3>
        <p>
          Six target industry and growth areas emerge as the most promising
          focus for the CEDS planning period: precision agriculture and
          agricultural technology; renewable energy development; healthcare and
          telehealth expansion; remote work and talent attraction; tourism and
          outdoor recreation economy; and value-added agriculture and food
          processing.
        </p>
        <p>
          These six areas are not solely a regional judgment call — they
          triangulate with two independent, external data sources. The Nebraska
          Statewide Workforce &amp; Educational Reporting System (NSWERS)
          identifies the state’s High Wage, High Skill, High Demand (H3)
          occupations with the largest unmet demand: among them, registered
          nurses and licensed practical/vocational nurses, teaching occupations,
          HVAC and refrigeration mechanics, and automotive service technicians
          all show high statewide demand outpacing the supply of qualified
          graduates. These occupations map directly onto Western Nebraska
          Community College’s existing program strengths in healthcare, skilled
          trades, and technical training (Chapter 7.3), meaning the workforce
          pipeline this CEDS proposes to build in Goal 2 is aimed at sectors the
          state’s own labor-market data confirms are under-supplied, not just
          sectors the region happens to already train for. The same occupations
          align closely with what the region’s own young people say they want:
          the Western Nebraska Teen Opportunities Survey found health science,
          agriculture, business, and skilled trades as the top four career
          interests among Panhandle teens (Chapter 11.4). When a regional
          employer-needs survey (proposed under Goal 2, Objective 2.2), a
          statewide labor-market demand model, and a 3,826-student regional teen
          survey all point toward the same handful of fields, the case for
          prioritizing them is no longer resting on any single source — it is
          the kind of convergent evidence this CEDS treats as firmly established
          (Chapter 11.1).
        </p>
        <p className="source-note">
          Source note: H3 occupation demand and unmet-demand figures from the
          NSWERS Brief on Employment Gap in Nebraska (2025), insights.nswers.org;
          WNCC program areas from Chapter 7.3; teen career-interest findings from
          the Western Nebraska Teen Opportunities Survey (2023), Chapter 11.4.
        </p>

        <h4>Renewable Energy Development</h4>
        <p>
          The Panhandle’s renewable energy opportunity is grounded in resource
          quality that is difficult to overstate: the region sits in the
          highest-solar-irradiance zone in Nebraska and ranks among the top
          solar resource areas nationally; Nebraska ranks fourth nationally for
          wind energy potential, and the Panhandle’s combination of consistent
          wind speeds, low population density, and available land makes it a
          strong candidate for utility-scale wind development. The region also
          produces more than half of Nebraska’s oil and natural gas — meaning
          the Panhandle is not transitioning away from an energy identity, but
          potentially adding a renewable dimension to an existing one.
        </p>
        <p>
          The economic development case rests on four revenue streams that are
          particularly significant for fiscally stressed frontier counties:
        </p>
        <ul className="ceds-list">
          <li>
            <strong>Construction employment</strong> — utility-scale wind and
            solar projects are labor-intensive during the build phase, creating
            temporary but well-compensated skilled-trades employment
            (electricians, ironworkers, equipment operators) that maps onto
            WNCC’s program strengths
          </li>
          <li>
            <strong>Operations and maintenance jobs</strong> — permanent O&amp;M
            positions are small in number but stable, technically skilled, and
            tend to pay above county median wages
          </li>
          <li>
            <strong>Landowner lease income</strong> — wind and solar lease
            payments provide a non-commodity income stream for agricultural
            landowners facing Ogallala depletion pressure and commodity price
            volatility
          </li>
          <li>
            <strong>Property-tax revenue</strong> — wind and solar installations
            generate real property tax revenue for counties and school districts
            with thin commercial tax bases; in some Great Plains counties, wind
            energy has become the largest single source of property-tax revenue
          </li>
        </ul>
        <p>
          The binding constraint is transmission capacity, documented in
          Chapter 4 (§4.5). No amount of resource quality converts to economic
          development without the infrastructure to move power to market.
          Federal transmission investment programs — the IRA’s transmission loan
          guarantee authority and the IIJA’s Grid Resilience and Innovation
          Partnerships — represent the most viable path to resolving this
          constraint, but timelines are long and competition for federal
          transmission investment is intense.
        </p>
        <p>
          A second constraint is community acceptance. Wind development has
          encountered organized opposition in some Nebraska communities; the
          SWOT analysis (Chapter 8) identifies this as a threat to wind lease
          and tax revenue. PADD’s appropriate role is not to advocate for any
          specific project but to ensure that communities have access to
          accurate information about economic impacts, royalty structures, and
          decommissioning requirements so that local decisions are made on
          evidence rather than on either boosterism or unfounded opposition.
        </p>

        <h4>Conventional Energy — An Existing Asset</h4>
        <p>
          While renewable energy is the growth opportunity, the Panhandle’s
          existing conventional energy base oil and gas production in Cheyenne,
          Kimball, Banner, and Deuel counties is a current economic asset that
          this CEDS does not overlook. Production activity supports direct
          employment, royalty income, and supply-chain spending. The coal
          transportation corridor via BNSF is an additional economic activity,
          though one facing long-term demand uncertainty as utilities shift
          generation mix. The conventional energy sector is not a target for new
          investment under this CEDS, but maintaining the conditions (regulatory
          clarity, infrastructure access, workforce availability) that allow
          existing operations to continue is consistent with the foundational
          goal of supporting the businesses already here.
        </p>
        <p className="source-note">
          Source note: Solar irradiance data from NREL; wind potential ranking
          from NREL Wind Energy Resource Atlas; Nebraska O&amp;G production share
          from Nebraska Oil and Gas Conservation Commission (NOGCC);
          transmission program references from DOE Loan Programs Office and
          FHWA IIJA implementation guidance.
        </p>

        <h3>5.8 Synthesis: Economic Development Implications</h3>
        <p>
          A CEDS strategy that concentrates investment in the highest-leverage
          opportunities — broadband-enabled agriculture and telehealth, tourism
          asset activation, remote-worker attraction, renewable energy
          development, and value-added agricultural processing — while
          maintaining the healthcare and education infrastructure that sustains
          community viability, is both realistic about the Panhandle’s
          constraints and ambitious about the potential those constraints do not
          foreclose. IRA and IIJA energy investment, the Nebraska Passport
          Program and ‘Nebraska. All Heart.’ campaign, and the remote-work
          economy share one characteristic: they reward early, sustained action.
          IRA transmission incentives remain subject to policy uncertainty. The
          strategic implication is urgency on energy and talent attraction — the
          Panhandle must engage decisively in the first two years of the
          planning period, not the last. BEAD broadband, by contrast, has
          shifted from a defined application window to an ongoing monitoring
          posture: Nebraska’s approved deployment covers approximately 88
          percent of eligible locations with significant program constraints,
          and PADD’s role is sustained advocacy, gap-fill identification, and
          ISP partnership — not a one-time sprint to submit applications before
          a deadline. The full Regional SWOT Analysis is presented in Chapter 8.
        </p>
      </div>
    </div>
  );
}
